import { useRef } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ViewSwitcherOption {
  value: string;
  label: string;
  /** Optional leading glyph (ADV-1359) — the Linear/Catalyst per-view icon. */
  icon?: LucideIcon;
}

interface ViewSwitcherProps {
  value: string;
  onValueChange: (next: string) => void;
  options: ViewSwitcherOption[];
  "aria-label": string;
}

/**
 * Three- to four-option view switcher. Native `role=tablist`/`role=tab`
 * implementation (not Radix) so keyboard semantics are explicit. Mirrors
 * `.view-switcher` from mockup--opportunity-list.css:193-225.
 */
export function ViewSwitcher({
  value,
  onValueChange,
  options,
  "aria-label": ariaLabel,
}: ViewSwitcherProps) {
  const tablistRef = useRef<HTMLDivElement>(null);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const idx = options.findIndex((o) => o.value === value);
    if (idx < 0) {
      if (import.meta.env.DEV) {
        console.warn(
          `ViewSwitcher: controlled value "${value}" is not in options; ignoring keyboard navigation.`,
        );
      }
      if (options.length > 0) {
        e.preventDefault();
        onValueChange(options[0].value);
      }
      return;
    }
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const next = (idx + delta + options.length) % options.length;
    e.preventDefault();
    onValueChange(options[next].value);
    // Focus the new tab on next frame so consumers can re-render first.
    requestAnimationFrame(() => {
      const buttons =
        tablistRef.current?.querySelectorAll<HTMLButtonElement>(
          "button[role='tab']",
        );
      buttons?.[next]?.focus();
    });
  }

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        "inline-flex overflow-hidden",
        "rounded-[var(--radius-sm)] border border-[color:var(--color-border)]",
      )}
    >
      {options.map((opt, i) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              if (!selected) onValueChange(opt.value);
            }}
            className={cn(
              "typo-label inline-flex items-center gap-1 px-3 py-1",
              "uppercase",
              "bg-[color:var(--color-surface)] text-[color:var(--color-text-md)]",
              "hover:bg-[color:var(--color-elevated)] hover:text-[color:var(--color-text-hi)]",
              "transition-colors cursor-pointer",
              i > 0 && "border-l border-[color:var(--color-border)]",
              selected &&
                "bg-[color-mix(in_srgb,var(--color-accent)_8%,transparent)] text-[color:var(--color-accent)] font-medium",
            )}
          >
            {opt.icon && <opt.icon className="h-3.5 w-3.5" aria-hidden />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
